/**
 * PGSS-5748: what a 4" × 6" box label shows. Pure helpers, so the label's rules are tested without rendering it.
 * Agreed with PSG on 18 Oct 2026: customer code at the top, QR code of the box code with the code printed under it, and
 * "USA VAS" / "PLANT VAS" at the bottom for an order with VAS. The classic label's company header and its labelled
 * colour / shape / finish and LEN / THK / HGT cells stay on it too.
 */

/** The box code as printed: groups of four, e.g. 7K3M-9QX2-TA. Anything that isn't a 10-character code is left as is. */
export function formatBoxCode(code) {
  if (typeof code !== 'string' || code.length !== 10) return code || '';
  return `${code.slice(0, 4)}-${code.slice(4, 8)}-${code.slice(8)}`;
}

/** "USA VAS" or "PLANT VAS" for an order with VAS, otherwise nothing. */
export function vasText(vasLocation) {
  if (vasLocation === 'USA') return 'USA VAS';
  if (vasLocation === 'PLANT') return 'PLANT VAS';
  return null;
}

/**
 * PGSS-5792: what the customer block shows. The customer's code; "Inventory" for PSG stock (no customer, flagged by
 * the API as is_inventory); otherwise a dash, as for a customer with no code.
 */
export function customerText(label) {
  if (label?.customer_code) return label.customer_code;
  return label?.is_inventory ? 'Inventory' : '—';
}

/** Lines break between words, so a shrunk value needs about a tenth more room than its bare character count. */
const WRAP_ROOM = 1.1;

/**
 * PGSS-5748 (QA, 21 Oct): a long value shrinks to fit its cell instead of being cut with "…". A cell holds about
 * `charsPerLine` characters on each of `lines` lines at `basePx`. At a smaller size s, a line holds basePx / s times
 * as many characters, and the cell's height holds floor(lines * basePx / s) whole lines. The size starts from the
 * square-root estimate and steps down until those whole lines hold the text with a little room for words that wrap
 * early (the normal size needs that room too); the line limit is then everything the cell holds at that size, never more, so a long value can't push the
 * label onto a second page. It never goes below `minPx`; only text too long even at that size is cut.
 */
export function fitText(text, { basePx, charsPerLine, lines, minPx = 8 }) {
  const length = String(text ?? '').length;
  if (length * WRAP_ROOM <= charsPerLine * lines) return { fontPx: basePx, lines };
  const linesAt = (px) => Math.floor((lines * basePx) / px);
  const holds = (px) => linesAt(px) * ((charsPerLine * basePx) / px) >= length * WRAP_ROOM;
  let fontPx = Math.max(minPx, Math.floor(basePx * Math.sqrt((charsPerLine * lines) / length)));
  while (fontPx > minPx && !holds(fontPx)) fontPx -= 1;
  return { fontPx, lines: Math.max(lines, linesAt(fontPx)) };
}

/** The value the QR code holds: the box code alone, never a name or price. */
export function qrValue(label) {
  return label?.box_code || '';
}

/**
 * The order number a label shows: the customer order's, PSG's own number for it (a staff print from the order sends
 * it as customer_order_no). The supplier print sends only order_no, which is the same number (customer order and its
 * supplier order share it), so the supplier never sees anything new.
 */
export function orderNumberText(label) {
  const number = label?.customer_order_no || label?.order_no;
  return number ? `#${number}` : '';
}
